import {
  CrudMode,
  DataDisplay,
  Grid,
  pn,
  Stack,
  Divider,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { Input, Select } from "components";
import {
  Permission,
  StudentServiceIndex,
  StudentServiceIndexEditResource,
  StudentServiceIndexViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { FieldWrapper } from "components/common/FieldWrapper";

export const StudentServiceIndexPageBody = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  index: StudentServiceIndex;
  setIndex: (value: StudentServiceIndex) => void;
  viewResource: StudentServiceIndexViewResource;
  editResource: StudentServiceIndexEditResource;
  validationErrors: ValidationErrors;
  onEdit?: () => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const serviceSubTypes = props.viewResource.serviceSubTypes?.filter(
    (x) => x.serviceTypeId === props.index.serviceTypeId
  );

  const activityField = () => {
    const propertyName = pn<StudentServiceIndex>("activity");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.index?.activity}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.index.activity}
        onChange={(e) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.activity = e.target.value;
            })
          )
        }
      />
    );
  };

  const nameField = () => {
    const propertyName = pn<StudentServiceIndex>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.index?.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.index.name}
        onChange={(e) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentServiceIndex>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" spacing="sm" alignItems="center">
          <Typography variant="inherit">{props.index.serviceType?.name}</Typography>
          {props.index.serviceType?.isForParent && <EscalatorWarningIcon />}
        </Stack>
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.serviceTypes?.find((x) => x.id === props.index.serviceTypeId)}
        options={props.viewResource.serviceTypes}
        groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
              //draft.parentCount = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentServiceIndex>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.index.serviceType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.index.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const providerField = () => {
    const propertyName = pn<StudentServiceIndex>("providerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.index.provider && (
          <>
            {props.index.provider.lastName}, {props.index.provider.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.providers?.find((x) => x.id === props.index.providerId)}
        options={props.editResource.providers}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.providerId = value?.id;
              draft.serviceSubTypeId = undefined;
            })
          )
        }
      />
    );
  };

  const durationField = () => {
    const propertyName = pn<StudentServiceIndex>("duration");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.index?.duration}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.index.duration}
        onChange={(e) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.duration = e.target.value;
            })
          )
        }
      />
    );
  };

  const serviceLocationField = () => {
    const propertyName = pn<StudentServiceIndex>("serviceLocation");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.index?.serviceLocation}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.index.serviceLocation}
        onChange={(e) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.serviceLocation = e.target.value;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<StudentServiceIndex>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.index.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.index.note}
        onChange={(e) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const serviceDescriptionField = () => {
    const propertyName = pn<StudentServiceIndex>("serviceDescription");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.index.serviceDescription}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.index.serviceDescription}
        onChange={(e) =>
          props.setIndex(
            produce(props.index, (draft) => {
              draft.serviceDescription = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Grid>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={12}>
          <Typography variant="inherit" >Service</Typography>
          <Divider />
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {activityField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          <FieldWrapper>
            {serviceTypeField()}
          </FieldWrapper>
        </Grid>

        {serviceSubTypes && serviceSubTypes.length > 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            <FieldWrapper>
              {serviceSubTypeField()}
            </FieldWrapper>
          </Grid>
        )}

        <Grid item xs={smallFieldBreakpoints}>
          <FieldWrapper>
            {nameField()}
          </FieldWrapper>
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          <FieldWrapper>
            {providerField()}
          </FieldWrapper>
        </Grid>
      </Grid>

      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={12}>
          <Typography variant="inherit" mt={4}>Information</Typography>
          <Divider />
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {durationField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {serviceLocationField()}
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {serviceDescriptionField()}
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {noteField()}
        </Grid>
      </Grid>
    </Grid>
  );
};
